import { useRef, useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { Upload, X, FileText } from "lucide-react";
import { storage } from "@/lib/firebase";

// Single-file uploader for a listing floorplan — accepts a PDF or an image and
// stores its download URL. Themed for A Homely Abode.
//   path     — Storage folder, e.g. "media/listings/abc123"
//   value    — current floorplan URL (string)
//   onChange — (url) => void

const isPdf = (u = "") => u.toLowerCase().includes(".pdf");

export default function FloorplanUploader({ path, value = "", onChange, testid = "floorplan-uploader" }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  const handleFile = (file) => {
    setError("");
    if (!file) return;
    const okType = file.type.startsWith("image/") || file.type === "application/pdf";
    if (!okType) { setError("Upload a PDF or image file."); return; }
    const ext = (file.name.match(/\.[a-z0-9]+$/i)?.[0] || "").toLowerCase();
    const key = `${path}/floorplan-${Date.now()}${ext}`;
    const task = uploadBytesResumable(ref(storage, key), file, { contentType: file.type });
    task.on(
      "state_changed",
      (snap) => setProgress((snap.bytesTransferred / snap.totalBytes) * 100),
      (err) => {
        // eslint-disable-next-line no-console
        console.error("Floorplan upload failed", err);
        setError(`Upload failed: ${err.code || err.message}`);
        setProgress(null);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onChange(url);
        setProgress(null);
      }
    );
  };

  const remove = async () => {
    const url = value;
    onChange("");
    try { await deleteObject(ref(storage, url)); } catch { /* best-effort */ }
  };

  return (
    <div data-testid={testid}>
      {value ? (
        <div className="flex items-center gap-3 border border-bronze/20 bg-beige/40 p-3">
          {isPdf(value) ? (
            <div className="h-16 w-16 flex items-center justify-center bg-cream border border-bronze/20">
              <FileText className="text-bronze" size={22} />
            </div>
          ) : (
            <img src={value} alt="Floorplan" className="h-16 w-16 object-cover border border-bronze/20" />
          )}
          <a href={value} target="_blank" rel="noreferrer" className="text-sm text-bronze underline flex-1 truncate">
            {isPdf(value) ? "View PDF floorplan" : "View floorplan image"}
          </a>
          <button type="button" onClick={remove} title="Remove" data-testid={`${testid}-remove`} className="text-red-600 hover:text-red-800 p-1.5">
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={progress != null}
          data-testid={`${testid}-add`}
          className="w-full h-24 border-2 border-dashed border-bronze/30 hover:border-bronze hover:bg-beige/40 flex flex-col items-center justify-center text-olive transition-colors disabled:opacity-50"
        >
          <Upload size={18} className="mb-1.5" />
          <span className="text-xs font-medium">{progress != null ? `Uploading ${Math.round(progress)}%` : "Upload floorplan (PDF or image)"}</span>
        </button>
      )}
      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-2 mt-2">{error}</div>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}
